import React from "react";
import {
  fuHeadView, fuRestEmpty, fuRowView, fuTitle, openFuRows, planHeading, planLineView, reviewedFuThisVisit, visibleRows,
} from "./followUpModel";
import { clockTime } from "./recsModel";
import { useOrdersShown } from "./ordersShown";
import { rowRecord } from "./sourceLinks";
import "./followup.css";

/*
 * Follow up · since <last visit> (CONTRACT §3.3, §4.3-4.6; careiq-final).
 *   full  every open row (critical first), the last visit's plan, its lines
 *   lead  the first open row + "N more · Show" (or "N more below" when the
 *         rest heads the timeline under the drawer: stacked screens, before
 *         recording)
 *   line  "Follow up · since Sep 11 · all 5 reviewed today · Show"
 * part "lead" / "rest": the stacked split (lead above the drawer, the rest at
 * the head of the timeline).
 * The timeline head (full before recording on desktop, and the rest of a
 * stacked split) holds the critical row(s) + at most 2 more items, then
 * "N more · Show" (fuHeadView): the patient's own timeline stays in view.
 * Records open in place, like a timeline row: the whole row is the button.
 * A document row opens the document viewer (one row per multi-take upload:
 * "Electrocardiogram" · "3 files" opens the newest file; the timeline hides
 * every take while the row is offered, D2); a lab row and a folded lab line
 * open the lab popup of that result date (the row's `open` in careiq.n3.2,
 * its own date in n3.1).
 * Readability (D3, fuRowView + followup.css): a muted kind icon, the item
 * semibold, its value bold + tabular and coloured by status (critical red +
 * a "Critical" chip, abnormal amber, both with ↑ / ↓), one grey meta line,
 * at most one clause-clipped AI line; reviewed entries muted.
 */

const labRecord = (row) => {
  const r = rowRecord(row);
  return r && r.kind === "lab" ? r : null;
};

const RING = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="10" cy="10" r="8.25" />
    <path d="m6.6 10.2 2.3 2.3 4.6-4.8" />
  </svg>
);

// Wrap between " · " segments, never inside one.
const Segments = ({ text }) =>
  String(text || "").split(" · ").map((x, i) => (
    <React.Fragment key={i}>
      {i > 0 ? " · " : null}
      <span className="n3-nw">{x}</span>
    </React.Fragment>
  ));

const KIND_LABEL = { lab: "Lab", imaging: "Imaging", doc: "Document", hospital: "Hospital / ED", med: "Medication" };
const ICON_PATHS = {
  lab: "M9 3h6M10 3v6.2L5.2 17.6A2.2 2.2 0 0 0 7.1 21h9.8a2.2 2.2 0 0 0 1.9-3.4L14 9.2V3M7.6 14h8.8",
  imaging: "M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16M7 12h2.6l1.4-3 2.4 6 1.4-3H17",
  doc: "M14 3H7.5A1.5 1.5 0 0 0 6 4.5v15A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V7zM14 3v4h4M9 12.5h6M9 16h4",
  hospital: "M4 21V8.5L12 4l8 4.5V21M3 21h18M12 9.5v6M9 12.5h6",
  med: "M10.6 19.4a4.6 4.6 0 0 1-6.5-6.5l5.8-5.8a4.6 4.6 0 0 1 6.5 6.5zM7 10l7 7",
};
// The kind cue: a small muted icon (plan and pending-order rows keep their ring).
const KindIcon = ({ kind }) =>
  ICON_PATHS[kind] ? (
    <span className="n3-fu-k" aria-hidden="true" title={KIND_LABEL[kind]}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d={ICON_PATHS[kind]} />
      </svg>
    </span>
  ) : null;

// A value with its status: tabular, coloured by sev, ↑ / ↓ (and the word for a screen reader);
// with no direction, the word "abnormal": the colour is never alone.
const Value = ({ value, dir, sev, flag }) => (
  <span className={`n3-fu-v${sev ? ` sev-${sev}` : ""}`} data-sev={sev || undefined}>
    {value}
    {dir === "high" || dir === "low" ? (
      <>
        <span className="n3-fu-dir" aria-hidden="true" data-dir={dir}>{dir === "high" ? "↑" : "↓"}</span>
        <span className="n3-sr">{` ${dir}`}</span>
      </>
    ) : flag === "abnormal" ? <span className="n3-fu-flag">{value ? " abnormal" : "abnormal"}</span> : null}
  </span>
);

export function FollowUpRow({ row, lead, ctx }) {
  // orders here: the practice's (7825: none) and only on today's live visit (N3/ordersShown.js)
  const ordersOn = useOrdersShown();
  const crit = row.group === "critical";
  const isDoc = row.kind === "fu_doc" && Boolean(row.doc);
  const v = fuRowView(row, ctx.refYear);
  const reviewed = row.status === "reviewed";
  // The critical row always shows its other results; other lead rows the grey line only.
  const brief = lead && !crit;
  const detail = brief ? [] : v.detail;
  // What the row opens: a document (the viewer) or a lab result date (the popup).
  const lab = !isDoc && ctx.onOpenRecord ? labRecord(row) : null;
  const opens = isDoc ? Boolean(row.doc.open_report && ctx.onOpenReport) : Boolean(lab);
  const onOpen = isDoc ? () => ctx.onOpenReport(row) : () => ctx.onOpenRecord(rowRecord(row, ctx.refYear));
  const openLabel = isDoc
    ? `Open ${row.doc.category === "imagingresult" ? "imaging report" : "document"}: ${row.short}`
    : `Open results: ${row.short}`;
  const title = (
    <>
      {!opens && KIND_LABEL[v.kind] ? <span className="n3-sr">{`${KIND_LABEL[v.kind]}: `}</span> : null}
      <span className="n3-fu-name">{v.name}</span>
      {v.value ? <>{" "}<Value value={v.value} dir={v.dir} sev={v.sev} flag={v.flag} /></> : null}
      {v.chip ? <>{" "}<span className="n3-fu-chip" data-chip="critical">{v.chip}</span></> : null}
    </>
  );
  const ring = !v.kind || v.kind === "plan" || v.kind === "order";
  const info = row.action ? ctx.infoFor(row) : null;
  return (
    <div
      className={`n3-fu-row${opens ? " n3-fu-opens" : ""}${crit ? " crit" : ""}${lead ? " n3-lead" : ""}${reviewed ? " is-reviewed" : ""}${ctx.flashKey === row.key ? " flash" : ""}`}
      data-fu={row.key}
      data-kind={v.kind || undefined}
      data-sev={v.sev || undefined}
    >
      {ring ? <span className={`n3-fu-dot${crit ? " crit" : row.group === "plan" ? " n3-ring" : ""}`} /> : <KindIcon kind={v.kind} />}
      <div className="n3-fu-body">
        <div className="n3-fu-th">
          {opens ? (
            // the whole row is this button (its ::after covers the row); Reviewed / Add sit above it
            <button type="button" className="n3-fu-t n3-fu-open no-hover-tint" data-act={isDoc ? "fuDoc" : "fuLab"} aria-label={openLabel} onClick={onOpen}>
              {title}
            </button>
          ) : (
            <div className="n3-fu-t">{title}</div>
          )}
          {ctx.stateOk && row.status === "open" && (
            <button
              type="button"
              className="n3-fu-rv no-hover-tint"
              data-act="fuRev"
              aria-label={`Mark reviewed: ${row.short}`}
              title="Mark reviewed"
              onClick={() => ctx.onReview(row)}
            >
              {RING}
              <span>Reviewed</span>
            </button>
          )}
        </div>
        {v.values.length && !brief ? (
          <div className="n3-fu-vals">
            {v.values.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <span className="n3-fu-sep">{" · "}</span> : null}
                <span className="n3-nw">
                  {b.name}
                  {b.value || b.dir || b.flag ? <>{" "}<Value value={b.value} dir={b.dir} sev="abnormal" flag={b.flag} /></> : null}
                </span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {v.meta.length ? (
          <div className="n3-fu-s n3-fu-meta">
            <Segments text={v.meta.join(" · ")} />
          </div>
        ) : null}
        {detail.map((l, i) => (
          <div className="n3-fu-detail" key={i}>
            <Segments text={l} />
          </div>
        ))}
        {v.ai && !lead ? <div className="n3-fu-ai" title={row.ai_quote !== v.ai ? row.ai_quote : undefined}>{v.ai}</div> : null}
        {/* no Add where the practice places no CareIQ orders (7825); an order already on the visit still says so */}
        {row.action && info && (info.state === "added" || ordersOn) && (
          <div className="n3-fu-acts act">
            {info.state === "added" ? (
              <span className="n3-fu-done" data-fu-done={row.key}>
                <span>{info.text}</span>
                {info.canUndo && ordersOn && (
                  <>
                    <span>·</span>
                    <button type="button" className="n3-lnk no-hover-tint" data-act="fuUndoAdd" onClick={() => ctx.onUndoAdd(row)}>Undo</button>
                  </>
                )}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="n3-btn-out sm"
                  data-act="fuAdd"
                  disabled={Boolean(info.busy)}
                  aria-label={row.action.label}
                  title={`Adds ${row.action.ordername} to this visit’s orders`}
                  onClick={() => ctx.onAdd(row)}
                >
                  {/* one line capped to the row with "…" (CareIQTabN3.css .n3-btn-l) */}
                  <span className="n3-btn-l">{row.action.label}</span>
                </button>
                {info.error ? <span className="n3-err">{info.error}</span> : null}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FuRev({ rev, ctx }) {
  if (!rev.length || !ctx.stateOk) return null;
  return (
    <div className="n3-fu-rev">
      <button type="button" className="no-hover-tint" data-act="fuToggleRev" aria-expanded={ctx.revOpen} onClick={ctx.onToggleRev}>
        Reviewed today ({rev.length}) {ctx.revOpen ? "▴" : "▾"}
      </button>
      <span>·</span>
      <button type="button" className="no-hover-tint" data-act="fuUndo" onClick={ctx.onUndoLast}>Undo</button>
      {ctx.revOpen && (
        <div className="n3-fu-rlist">
          {rev.map((r) => (
            // a reviewed row is muted: grey, regular weight, a quiet ✓ (no status colour)
            <div className="n3-fu-rrow is-reviewed" data-reviewed="true" key={r.key}>
              <span className="n3-fu-ck" aria-hidden="true">✓</span>
              <span>{r.short} · reviewed {clockTime(r.reviewed && r.reviewed.at)}{r.reviewed && r.reviewed.by && r.reviewed.by !== ctx.me ? ` by ${r.reviewed.by}` : ""}</span>
              <button type="button" className="no-hover-tint" onClick={() => ctx.onUndoRow(r)}>Undo</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FollowUp({ body, mode, part, ctx }) {
  const title = fuTitle(body);
  const open = openFuRows(body);
  const rev = reviewedFuThisVisit(body);
  const visible = visibleRows(body);

  if (mode === "line") {
    return (
      <div className="n3-since-line" data-testid="n3-fu-line">
        {visible.length ? (
          <>
            {title} · all {visible.length} reviewed today ·{" "}
            <button type="button" className="no-hover-tint" data-act="fuShow" onClick={ctx.onShow}>Show</button>
          </>
        ) : (
          <>{title} · nothing new</>
        )}
      </div>
    );
  }

  if (mode === "lead" && part !== "rest") {
    const lead = open[0];
    const more = open.length - 1;
    let btn = null;
    if (more > 0) {
      btn = part === "lead" ? (
        <button type="button" className="n3-since-all no-hover-tint" data-act="fuBelow" title="The rest of Follow up heads the Patient Timeline, below AI Recommendations" onClick={ctx.onBelow}>
          {more} more below
        </button>
      ) : (
        <button type="button" className="n3-since-all no-hover-tint" data-act="fuOpen" aria-expanded="false" onClick={ctx.onOpen}>
          {more} more · Show
        </button>
      );
    }
    return (
      <div className="n3-since" data-testid="n3-fu-lead">
        <div className="n3-since-h"><span className="n3-since-t">{title}</span>{btn}</div>
        {lead ? <FollowUpRow row={lead} lead ctx={ctx} /> : null}
        <FuRev rev={rev} ctx={ctx} />
      </div>
    );
  }

  // full, or the "rest" of a stacked split: the head holds the critical
  // row(s) + 2 more, the rest behind "N more · Show" (fuHeadView)
  const fuExpanded = Boolean(ctx.fuMore || ctx.fuOpen || ctx.fuShow);
  const hv = fuHeadView(body, { part, expanded: fuExpanded });
  const { nonPlan, planRows, lines, folded } = hv;
  const shown = [...nonPlan, ...planRows];
  const heading = planHeading(body);
  const rowsByKey = new Map((body.followup.rows || []).map((r) => [r.key, r]));
  // nothing left under the lead above the drawer: no empty block at the timeline's head
  if (part === "rest" && fuRestEmpty(body, ctx.stateOk)) return null;
  const btns = [];
  if (hv.capped) {
    btns.push(
      <button key="more" type="button" className="n3-since-all no-hover-tint" data-act="fuMore" aria-expanded="false" onClick={ctx.onMore}>
        {hv.hidden} more · Show
      </button>,
    );
  } else if (part !== "rest" && (!ctx.before || ctx.stacked) && open.length && ctx.fuOpen) {
    btns.push(<button key="close" type="button" className="n3-since-all no-hover-tint" data-act="fuClose" aria-expanded="true" onClick={ctx.onClose}>Show less</button>);
  } else {
    if (shown.length > 1 && ctx.stateOk) {
      btns.push(
        <button key="all" type="button" className="n3-since-all no-hover-tint" data-act="fuAll" aria-label="Mark all reviewed" onClick={() => ctx.onMarkAll(shown)}>
          Mark all<span className="wd"> reviewed</span>
        </button>,
      );
    }
    if (ctx.fuMore && hv.total > hv.budget) {
      btns.push(<button key="less" type="button" className="n3-since-all no-hover-tint" data-act="fuLess" aria-expanded="true" onClick={ctx.onLess}>Show less</button>);
    }
  }
  const hide = ctx.fuShow && !open.length
    ? <button type="button" className="n3-since-all no-hover-tint" data-act="fuHide" onClick={ctx.onHide}>Hide</button>
    : null;
  return (
    <div className="n3-since" data-testid={part === "rest" ? "n3-fu-rest" : "n3-fu-full"} data-capped={hv.capped ? "true" : undefined}>
      <div className="n3-since-h"><span className="n3-since-t">{title}</span>{btns}{hide}</div>
      {part === "rest" && open[0] ? (
        <div className="n3-fu-line above">
          <span className="g" aria-hidden="true">↑</span>
          <span>{open[0].short} — above AI Recommendations</span>
        </div>
      ) : null}
      {nonPlan.map((r) => <FollowUpRow key={r.key} row={r} ctx={ctx} />)}
      {heading && (planRows.length > 0 || lines.length > 0) ? <div className="n3-fu-sub">{heading}</div> : null}
      {planRows.map((r) => <FollowUpRow key={r.key} row={r} ctx={ctx} />)}
      {lines.map((l) => {
        const v = planLineView(l, { recsByKey: ctx.recsByKey, rowsByKey });
        return (
          <div className="n3-fu-line" key={l.key} data-fu-line={l.key}>
            <span className="g" aria-hidden="true">{v.mark}</span>
            <span>
              {v.text}
              {v.link ? (
                <button type="button" className="no-hover-tint" data-act="goBand" onClick={() => ctx.onGoBand(v.link.issue_key)}>{v.link.label}</button>
              ) : null}
            </span>
          </div>
        );
      })}
      {folded.length > 0 && (
        <>
          <div className="n3-fu-line">
            <span className="g" aria-hidden="true">+</span>
            <span>
              {folded.length} more normal result{folded.length === 1 ? "" : "s"} ·{" "}
              <button type="button" className="no-hover-tint" aria-expanded={ctx.foldedOpen} onClick={ctx.onToggleFolded}>
                {ctx.foldedOpen ? "Hide" : "Show"}
              </button>
            </span>
          </div>
          {ctx.foldedOpen && folded.map((r) => (
            <div className="n3-fu-line" key={r.key}>
              <span className="g" aria-hidden="true">·</span>
              <span>
                {ctx.onOpenRecord && labRecord(r) ? (
                  <button type="button" className="no-hover-tint" data-act="fuLab" aria-label={`Open results: ${r.short}`} onClick={() => ctx.onOpenRecord(rowRecord(r, ctx.refYear))}>{r.text}</button>
                ) : r.text}
                {r.sub && r.sub.length ? ` — ${r.sub.join(" · ")}` : ""}
              </span>
            </div>
          ))}
        </>
      )}
      <FuRev rev={rev} ctx={ctx} />
    </div>
  );
}
